import { GridReportViewThresholdComparator } from 'components/GridReport/models';

// Interface for threshold items
export interface ThresholdItem {
  id?: string;
  tempId?: string;
  color?: string;
  label?: string;
  value?: number | string;
  secondValue?: number | string;
  comparator?: GridReportViewThresholdComparator;
  GridReportViewId?: string;
  WorkspaceId?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Interface for threshold range
interface ThresholdRange {
  min: number;
  max: number;
}

/**
 * Converts a threshold to a range for easier comparison
 * @param threshold - The threshold item to convert
 * @returns A range object with min and max values
 */
const getThresholdRange = (threshold: ThresholdItem): ThresholdRange => {
  const value = parseFloat(String(threshold.value));
  const secondValue = threshold.secondValue
    ? parseFloat(String(threshold.secondValue))
    : null;

  switch (threshold.comparator) {
    case 'gte':
      return { min: value, max: Infinity };
    case 'gt':
      return { min: value + 0.001, max: Infinity };
    case 'lte':
      return { min: -Infinity, max: value };
    case 'lt':
      return { min: -Infinity, max: value - 0.001 };
    case 'eq':
      return { min: value, max: value };
    case 'btn':
      return secondValue
        ? { min: value, max: secondValue }
        : { min: value, max: value };
    default:
      return { min: -Infinity, max: Infinity };
  }
};

// Function to get comparator symbol
export const getComparatorSymbol = (comparator: string): string => {
  switch (comparator) {
    case 'gte':
      return '≥';
    case 'gt':
      return '>';
    case 'lte':
      return '≤';
    case 'lt':
      return '<';
    case 'eq':
      return '=';
    case 'btn':
      return '-';
    default:
      return comparator;
  }
};

/**
 * Checks if two thresholds overlap
 * @param t1 - First threshold item
 * @param t2 - Second threshold item
 * @returns true if the thresholds overlap, false otherwise
 */
export const checkThresholdOverlap = (
  t1: ThresholdItem,
  t2: ThresholdItem,
): boolean => {
  const range1 = getThresholdRange(t1);
  const range2 = getThresholdRange(t2);

  // Check for overlap: ranges overlap if max of one is >= min of the other
  return range1.max >= range2.min && range2.max >= range1.min;
};

/**
 * Validates a list of thresholds for overlaps
 * @param thresholds - Array of threshold items to validate
 * @returns true if no overlaps found, false if overlaps exist
 */
export const validateThresholdOverlaps = (
  thresholds: ThresholdItem[],
): boolean => {
  const validThresholds = thresholds.filter(
    (t) =>
      t.value !== undefined &&
      t.value !== null &&
      t.value !== '' &&
      (t.comparator !== 'btn' ||
        (t.secondValue !== undefined &&
          t.secondValue !== null &&
          t.secondValue !== '')),
  );

  for (let i = 0; i < validThresholds.length; i++) {
    for (let j = i + 1; j < validThresholds.length; j++) {
      const t1 = validThresholds[i];
      const t2 = validThresholds[j];

      if (checkThresholdOverlap(t1, t2)) {
        return false; // Overlap found
      }
    }
  }

  return true; // No overlaps found
};

/**
 * Validates thresholds for required fields and overlap
 * @param thresholds - Array of threshold items
 * @returns { isValid, errors, overlapError }
 */
export function validateThresholds(thresholds: ThresholdItem[]): {
  isValid: boolean;
  errors: Record<string, string>;
  overlapError: string;
} {
  const errors: Record<string, string> = {};
  let overlapMessage = '';

  // Check for empty values
  thresholds.forEach((threshold, index) => {
    const id = threshold.id || threshold.tempId || index.toString();

    if (!threshold.label?.trim()) {
      errors[`${id}_label`] = 'Label is required';
    }

    if (
      threshold.value === undefined ||
      threshold.value === null ||
      threshold.value === ''
    ) {
      errors[`${id}_value`] = 'Value is required';
    }

    // Validate second value for "between" comparator
    if (threshold.comparator === 'btn') {
      if (
        threshold.secondValue === undefined ||
        threshold.secondValue === null ||
        threshold.secondValue === ''
      ) {
        errors[`${id}_secondValue`] = 'Second value is required';
      }

      // Ensure first value is less than second value
      if (
        threshold.value !== undefined &&
        threshold.secondValue !== undefined &&
        parseFloat(String(threshold.value)) >=
          parseFloat(String(threshold.secondValue))
      ) {
        errors[`${id}_secondValue`] =
          'Second value must be greater than first value';
      }
    }
  });

  // Check for overlapping thresholds
  const validThresholds = thresholds.filter(
    (t) =>
      t.value !== undefined &&
      t.value !== null &&
      t.value !== '' &&
      (t.comparator !== 'btn' ||
        (t.secondValue !== undefined &&
          t.secondValue !== null &&
          t.secondValue !== '')),
  );

  for (let i = 0; i < validThresholds.length; i++) {
    for (let j = i + 1; j < validThresholds.length; j++) {
      const t1 = validThresholds[i];
      const t2 = validThresholds[j];

      if (checkThresholdOverlap(t1, t2)) {
        overlapMessage =
          'Threshold ranges overlap. Please ensure thresholds do not conflict with each other.';
        break;
      }
    }
    if (overlapMessage) break;
  }

  return {
    isValid: Object.keys(errors).length === 0 && !overlapMessage,
    errors,
    overlapError: overlapMessage,
  };
}
